import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { CategoryService, ColumnSeriesService, LineSeriesService, LegendService, TooltipService } from '@syncfusion/ej2-angular-charts';
import { TreeGridComponent, PageService, SortService, ResizeService, ToolbarService, ExcelExportService, PdfExportService } from '@syncfusion/ej2-angular-treegrid';
import { StaffWeightedWorkReport } from '../../../../shared/models/staff-weighted-work.model';
import { staffTargetDays, staffTargetDayLabel } from '../../../../shared/models/staff-reporting-calendar.model';

import { staffWorkDuration, staffWorkTimeHint } from '../../../../shared/functions/staff-work-duration';

import { achievementColors, countResultLabel } from '../achievements/achievement-presentation';
import { AchievementStatus } from '../../../../shared/models/staff-achievement.model';

let weightedInstance = 0;

@Component({
  selector: 'app-staff-weighted-work',
  templateUrl: './staff-weighted-work.component.html',
  styles: [`:host { display: block; } .ibox-title button { border: 0; background: transparent; width: 100%; text-align: left; }
    .metric { padding: 12px; } .metric strong { display: block; font-size: 24px; }
    .time-equivalent { display: block; color: #526172; font-size: 12px; margin-top: 4px; }
    .weighted-controls { margin: 10px 0; } .weighted-controls button { margin-right: 8px; }`],
  providers: [CategoryService, ColumnSeriesService, LineSeriesService, LegendService, TooltipService,
    PageService, SortService, ResizeService, ToolbarService, ExcelExportService, PdfExportService]
})
export class StaffWeightedWorkComponent implements OnChanges {
  readonly colors = achievementColors;
  gridRows: any[] = [];
  ngOnChanges(): void {
    if (!this.report) { this.gridRows = []; return; }
    const days = this.targetDays(this.report.context);
    this.gridRows = this.report.staff.map(staff => ({...staff, actions: staff.actions.map(action => {
      // Captured daily counts have two decimal places. Compare hundredths before
      // formatting, using this report's target days rather than current settings.
      const targetHundredths = Math.round(action.unit_per_day * 100) * days;
      const actualHundredths = action.count * 100;
      const status: AchievementStatus = targetHundredths <= 0 ? 'no_target' : actualHundredths < targetHundredths ? 'below' : actualHundredths > targetHundredths ? 'above' : 'on';
      return {...action, expected_count: targetHundredths / 100, count_difference: targetHundredths > 0 ? (actualHundredths - targetHundredths) / 100 : null,
        count_status: status, count_result_label: countResultLabel(status, action.unit_per_day)};
    })}));
  }
  readonly duration = staffWorkDuration;
  timeHint(value: number | null): string { return staffWorkTimeHint(value, this.report?.context.daily_capacity_minutes); }
  sharedTimeTooltip(args: any, kind: 'daily' | 'staff'): void {
    args.text = args.text.map((text: string, index: number) => {
      const series = args.series[index];
      if (series.yName === 'count') { return text; }
      const rows = kind === 'daily' ? this.report.daily : this.report.staff;
      const value = rows[args.point[index].index]?.[series.yName];
      const hint = this.timeHint(value);
      return hint ? text + ' (' + hint + ')' : text;
    });
  }
  readonly targetDays = staffTargetDays;
  readonly targetDayLabel = staffTargetDayLabel;
  @Input() report: StaffWeightedWorkReport | null = null;
  @Input() loading = false;
  @Input() error = '';
  @ViewChild('weightedGrid') grid: TreeGridComponent;
  readonly id = 'weighted-work-' + (++weightedInstance);
  open = [true, true, true, true];
  xAxis = { valueType: 'Category', title: 'Reporting date (Brisbane)', labelIntersectAction: 'Rotate45' };
  staffAxis = { valueType: 'Category', title: 'Staff', labelIntersectAction: 'Rotate45' };
  minutesAxis = { title: 'Minutes', minimum: 0, labelFormat: '{value}' };
  countAxes = [{ name: 'countAxis', opposedPosition: true, title: 'Action count', minimum: 0 }];
  tooltip = { enable: true, shared: true };
  legend = { visible: true };
  toolbar = ['ExcelExport', 'PdfExport'];
  pageSettings = { pageSize: 20, pageSizeMode: 'Root' };
  export(event: any): void {
    if (event.item.id.endsWith('_excelexport')) { this.grid.excelExport({ fileName: 'Weighted-Staff-Metrics.xlsx' }); }
    if (event.item.id.endsWith('_pdfexport')) { this.grid.pdfExport({ fileName: 'Weighted-Staff-Metrics.pdf', pageOrientation: 'Landscape' }); }
  }
}
